import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { requirementLabels } from '../../utils/payoutRequirements';
import '../../pages/DeveloperHome.css';

// Shared by Home and Money: the payout account as a checklist. Its rows use
// Home's `devhome__ck*` classes so the two pages draw it identically.

/**
 * GETTING PAID — the payout account as the four things selling needs.
 * `account` is undefined while unknown (or payments are off on this instance):
 * then the section is not shown, because telling someone to set up an account
 * they may already have is worse than silence.
 */
export function payoutChecklist(account, pricedApps, { brief = false } = {}) {
  const due = requirementLabels(account?.requirements_due);
  const ready = !!account?.can_charge;
  return [
    {
      id: 'account',
      name: 'Payout account',
      state: account ? 'done' : 'missing',
      sub: account
        ? `Stripe Express${account.country ? `, ${account.country}` : ''}${account.default_currency ? `, ${String(account.default_currency).toUpperCase()}` : ''}`
        : 'Until you have one, a paid app of yours cannot be bought.',
      action: account ? null : { label: 'Set up payouts', to: '/billing' },
    },
    {
      id: 'verify',
      name: 'Stripe verification',
      state: ready ? 'done' : account ? 'missing' : 'waiting',
      sub: ready ? 'Stripe has everything it needs.'
        : account ? (due.length
          ? (brief ? `Stripe still needs ${due.length} thing${due.length === 1 ? '' : 's'} from you, listed below.` : `Stripe still needs: ${due.join(', ').toLowerCase()}.`)
          : 'Stripe is still checking your details.')
        : 'Comes after the payout account.',
      action: account && !ready ? { label: 'Finish on Stripe', to: '/billing' } : null,
    },
    {
      id: 'priced',
      name: 'A priced app',
      state: pricedApps.length ? 'done' : 'waiting',
      sub: pricedApps.length
        ? (!brief ? pricedApps.map((a) => a.name).join(', ') : `${pricedApps.length} priced app${pricedApps.length === 1 ? '' : 's'}`)
        : 'Set a price on an app\'s Pricing tab. Free apps never need payouts.',
      action: null,
    },
    {
      id: 'sell',
      name: 'Ready to sell',
      state: ready && pricedApps.length ? 'done' : 'waiting',
      sub: ready && pricedApps.length ? 'People can buy your apps.' : 'Needs all of the above.',
      action: null,
    },
  ];
}

/**
 * Getting paid, drawn: Done / Missing / Waiting, and a step's action where it
 * has one. Without `onGo` (Money, where the real buttons stand just below) the
 * steps carry their state only; `brief` counts the priced apps and Stripe's
 * outstanding requirements rather than repeating what the page lists below.
 */
export default function PayoutChecklist({ account, pricedApps, onGo, brief = false }) {
  const items = payoutChecklist(account, pricedApps || [], { brief });
  return (
    <ol className="devhome__ck">
      {items.map((it, i) => (
        <li key={it.id} className="devhome__ck-row" data-state={it.state}>
          <span className="devhome__ck-n" aria-hidden="true">{it.state === 'done' ? <Check size={12} strokeWidth={2.6} /> : i + 1}</span>
          <span className="devhome__ck-words">
            <span className="devhome__ck-name">{it.name}</span>
            <span className="devhome__ck-sub">{it.sub}</span>
          </span>
          {it.action && onGo
            ? <button type="button" className="devhome__go" onClick={() => onGo(it.action.to)}>{it.action.label} <ArrowRight size={13} /></button>
            : <span className="devhome__pill" data-state={it.state}>{it.state === 'done' ? 'Done' : it.state === 'missing' ? 'Missing' : 'Waiting'}</span>}
        </li>
      ))}
    </ol>
  );
}
